import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lotteryAPI } from '../services/api';
import BuySharesModal from './BuySharesModal';

const GAME_COLORS = {
  'powerball': '#e53e3e',
  'ozlotto': '#dd6b20',
  'tattslotto': '#3182ce',
  'set-for-life': '#38a169',
  'mon-wed-lotto': '#805ad5',
  'default': '#f0c040',
};

function getGameColor(gameCode) {
  return GAME_COLORS[gameCode?.toLowerCase()] || GAME_COLORS.default;
}

function PublicLottery() {
  const navigate = useNavigate();
  const [draws, setDraws] = useState([]);
  const [myShares, setMyShares] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('draws');
  const [buyModalDraw, setBuyModalDraw] = useState(null);

  const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`;
  const formatJackpot = (cents) => {
    const dollars = cents / 100;
    if (dollars >= 1000000) return `$${(dollars / 1000000).toFixed(dollars % 1000000 === 0 ? 0 : 1)}M`;
    if (dollars >= 1000) return `$${(dollars / 1000).toFixed(0)}K`;
    return `$${dollars.toFixed(0)}`;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [drawsRes, sharesRes, historyRes] = await Promise.allSettled([
        lotteryAPI.getPublicDraws(),
        lotteryAPI.getPublicShares(),
        lotteryAPI.getPublicHistory(),
      ]);
      setDraws(drawsRes.status === 'fulfilled' ? (drawsRes.value.draws || drawsRes.value.upcoming || []) : []);
      setMyShares(sharesRes.status === 'fulfilled' ? (sharesRes.value.shares || []) : []);
      setHistory(historyRes.status === 'fulfilled' ? (historyRes.value.history || []) : []);
    } catch (err) {
      console.error('Error loading public lottery data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleBuySuccess = () => {
    setBuyModalDraw(null);
    loadData();
  };

  const renderNumberBall = (num, isSupplementary = false) => (
    <span
      key={`${num}-${isSupplementary ? 's' : 'm'}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '32px', height: '32px', borderRadius: '50%', fontSize: '13px', fontWeight: '700',
        backgroundColor: isSupplementary ? '#6b7280' : '#f0c040',
        color: isSupplementary ? '#fff' : '#1a1d23',
      }}
    >
      {num}
    </span>
  );

  const renderProgressBar = (sold, total) => {
    const pct = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
    return (
      <div style={{ height: '6px', backgroundColor: '#2a2d35', borderRadius: '3px', overflow: 'hidden', marginTop: '4px' }}>
        <div style={{ height: '100%', backgroundColor: '#f0c040', borderRadius: '3px', width: `${pct}%`, transition: 'width 0.3s' }} />
      </div>
    );
  };

  const tabs = [
    { key: 'draws', label: 'Upcoming Draws' },
    { key: 'shares', label: 'My Shares' },
    { key: 'results', label: 'Results' },
  ];

  return (
    <div style={styles.wrapper}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src="/logo.png" alt="Punt Club" style={{ height: '40px' }} />
          <div>
            <h1 style={styles.title}>Punt Club Lotto Syndicates</h1>
            <p style={styles.subtitle}>Join a syndicate for any upcoming draw. Buy shares and split the winnings!</p>
          </div>
        </div>
        <button onClick={() => navigate('/dashboard')} style={styles.backBtn}>
          &larr; Dashboard
        </button>
      </div>

      {/* Tab nav */}
      <div style={styles.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...styles.tabBtn,
              color: activeTab === tab.key ? '#f0c040' : '#888',
              borderBottomColor: activeTab === tab.key ? '#f0c040' : 'transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.loading}>Loading syndicates...</div>
      ) : (
        <>
          {/* Draws Tab */}
          {activeTab === 'draws' && (
            <div>
              {draws.length === 0 ? (
                <div style={styles.emptyState}>
                  <p style={{ color: '#888', fontSize: '15px' }}>No upcoming public draws at the moment. Check back soon!</p>
                </div>
              ) : (
                <div style={styles.drawGrid}>
                  {draws.map((draw) => {
                    const gameColor = getGameColor(draw.gameCode);
                    const totalShares = draw.maxShares || draw.totalShares || 10;
                    const sold = draw.sharesSold || 0;
                    const remaining = totalShares - sold;
                    const past = draw.drawDate && new Date(draw.drawDate) < new Date();
                    const full = remaining <= 0;

                    return (
                      <div key={draw.id} style={styles.drawCard}>
                        <div style={{ height: '4px', backgroundColor: gameColor }} />
                        <div style={{ padding: '16px' }}>
                          <div style={{ color: gameColor, fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>
                            {draw.gameName || draw.gameCode || 'Draw'}
                          </div>
                          <div style={{ color: '#999', fontSize: '13px', marginBottom: '8px' }}>
                            {draw.drawDate && new Date(draw.drawDate).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                            {draw.drawNumber && ` - Draw #${draw.drawNumber}`}
                          </div>
                          {(draw.jackpotCents || draw.jackpotEstimate) && (
                            <div style={{ color: '#4ade80', fontSize: '15px', fontWeight: '600', marginBottom: '10px' }}>
                              Est. Jackpot: {formatJackpot(draw.jackpotCents || draw.jackpotEstimate)}
                            </div>
                          )}
                          <div style={{ marginBottom: '10px' }}>
                            <span style={{ color: '#999', fontSize: '13px' }}>{sold} of {totalShares} shares sold</span>
                            {renderProgressBar(sold, totalShares)}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#ccc', fontSize: '14px' }}>
                              {formatCurrency(draw.sharePriceCents || draw.sharePrice || 500)} / share
                            </span>
                            <button
                              disabled={past || full}
                              onClick={() => setBuyModalDraw(draw)}
                              style={{
                                padding: '8px 16px', borderRadius: '6px', border: 'none', fontSize: '13px', fontWeight: '600',
                                cursor: past || full ? 'not-allowed' : 'pointer',
                                backgroundColor: past || full ? '#3a3d45' : '#f0c040',
                                color: past || full ? '#888' : '#1a1d23',
                              }}
                            >
                              {full ? 'Sold Out' : past ? 'Closed' : 'Buy Shares'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* My Shares Tab */}
          {activeTab === 'shares' && (
            <div>
              <h2 style={{ color: '#fff', fontSize: '18px', marginBottom: '12px' }}>My Shares</h2>
              {myShares.length === 0 ? (
                <div style={styles.emptyState}>
                  <p style={{ color: '#888' }}>You have no public syndicate shares yet.</p>
                  <p style={{ color: '#666', fontSize: '13px' }}>Buy shares in upcoming draws to participate.</p>
                </div>
              ) : (
                <div style={{ backgroundColor: '#1a1d23', borderRadius: '10px', border: '1px solid #2a2d35', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', padding: '10px 16px', borderBottom: '1px solid #2a2d35', backgroundColor: '#22252d' }}>
                    <span style={styles.headerCell}>Draw</span>
                    <span style={styles.headerCell}>Shares</span>
                    <span style={styles.headerCell}>Cost</span>
                    <span style={styles.headerCell}>Winnings</span>
                  </div>
                  {myShares.map((share, idx) => (
                    <div key={share.id || idx} style={{ display: 'flex', padding: '12px 16px', borderBottom: '1px solid #1f2229' }}>
                      <span style={{ flex: 1, fontSize: '14px' }}>
                        <span style={{ color: getGameColor(share.draw?.gameCode || share.gameCode), fontWeight: '600' }}>
                          {share.draw?.gameName || share.gameName || 'Draw'}
                        </span>
                        <br />
                        <span style={{ color: '#888', fontSize: '12px' }}>
                          {(share.draw?.drawDate || share.drawDate) ? new Date(share.draw?.drawDate || share.drawDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : ''}
                        </span>
                      </span>
                      <span style={{ flex: 1, fontSize: '14px', color: '#ccc' }}>{share.shares || 1}</span>
                      <span style={{ flex: 1, fontSize: '14px', color: '#ccc' }}>{formatCurrency(share.costCents || 0)}</span>
                      <span style={{ flex: 1, fontSize: '14px', color: (share.winningsCents || 0) > 0 ? '#4ade80' : '#888', fontWeight: (share.winningsCents || 0) > 0 ? '600' : '400' }}>
                        {(share.winningsCents || 0) > 0 ? formatCurrency(share.winningsCents) : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <div>
              <h2 style={{ color: '#fff', fontSize: '18px', marginBottom: '12px' }}>Recent Results</h2>
              {history.length === 0 ? (
                <div style={styles.emptyState}>
                  <p style={{ color: '#888' }}>No results yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {history.map((draw, idx) => {
                    const winNums = new Set(draw.winningNumbers || []);
                    const suppNums = new Set(draw.supplementaryNumbers || []);
                    const yourNums = draw.numbersSelected || [];
                    const myShare = myShares.find(s => (s.draw?.id || s.drawId) === draw.id);
                    const matchCount = yourNums.filter(n => winNums.has(n)).length;
                    const suppMatchCount = yourNums.filter(n => suppNums.has(n)).length;

                    return (
                      <div key={draw.id || idx} style={{ backgroundColor: '#1a1d23', borderRadius: '10px', border: '1px solid #2a2d35', padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                          <span style={{ color: getGameColor(draw.gameCode), fontWeight: '600', fontSize: '16px' }}>
                            {draw.gameName || draw.gameCode || 'Draw'}
                          </span>
                          <span style={{ color: '#888', fontSize: '13px' }}>
                            {draw.drawDate ? new Date(draw.drawDate).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                            {draw.drawNumber ? ` - Draw #${draw.drawNumber}` : ''}
                          </span>
                        </div>

                        {draw.winningNumbers && draw.winningNumbers.length > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                            <span style={{ color: '#888', fontSize: '13px' }}>Winning:</span>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {draw.winningNumbers.map((n) => renderNumberBall(n))}
                              {(draw.supplementaryNumbers || []).map((n) => renderNumberBall(n, true))}
                            </div>
                          </div>
                        )}

                        {yourNums.length > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                            <span style={{ color: '#888', fontSize: '13px' }}>Yours:</span>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {yourNums.map((n, i) => (
                                <span
                                  key={`yours-${n}-${i}`}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: '32px', height: '32px', borderRadius: '50%', fontSize: '13px', fontWeight: '700',
                                    backgroundColor: winNums.has(n) ? '#22c55e' : suppNums.has(n) ? '#3b82f6' : '#374151',
                                    color: winNums.has(n) || suppNums.has(n) ? '#fff' : '#888',
                                    border: winNums.has(n) ? '2px solid #4ade80' : suppNums.has(n) ? '2px solid #60a5fa' : '2px solid transparent',
                                  }}
                                >
                                  {n}
                                </span>
                              ))}
                            </div>
                            <span style={{ fontSize: '12px', color: matchCount > 0 ? '#4ade80' : '#666', marginLeft: '8px' }}>
                              {matchCount} match{matchCount !== 1 ? 'es' : ''}{suppMatchCount > 0 ? ` + ${suppMatchCount} supp` : ''}
                            </span>
                          </div>
                        )}

                        {myShare && (
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 12px', marginTop: '8px', borderRadius: '8px',
                            background: (myShare.winningsCents || 0) > 0 ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.03)',
                            border: (myShare.winningsCents || 0) > 0 ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(255,255,255,0.05)',
                          }}>
                            <span style={{ color: '#888', fontSize: '12px' }}>
                              Your {myShare.shares} share{myShare.shares !== 1 ? 's' : ''} (cost {formatCurrency(myShare.costCents || 0)})
                            </span>
                            <span style={{ fontWeight: '700', fontSize: '15px', color: (myShare.winningsCents || 0) > 0 ? '#4ade80' : '#666' }}>
                              {(myShare.winningsCents || 0) > 0 ? `Won ${formatCurrency(myShare.winningsCents)}` : 'No win'}
                            </span>
                          </div>
                        )}

                        {(draw.totalWinningsCents || 0) > 0 && (
                          <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(74,222,128,0.1)', color: '#4ade80', fontSize: '14px', fontWeight: '600', textAlign: 'center' }}>
                            Syndicate Total: {formatCurrency(draw.totalWinningsCents)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Buy Shares Modal */}
      {buyModalDraw && (
        <BuySharesModal
          draw={buyModalDraw}
          onClose={() => setBuyModalDraw(null)}
          onSuccess={handleBuySuccess}
          isPublic={true}
        />
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    backgroundColor: '#0f1115',
    color: '#fff',
    padding: '20px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 4px 0',
    color: '#fff',
  },
  subtitle: {
    color: '#888',
    fontSize: '14px',
    margin: 0,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#f0c040',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '4px 0',
  },
  tabNav: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: '1px solid #2a2d35',
  },
  tabBtn: {
    padding: '10px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
  },
  loading: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#888',
    fontSize: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: '#1a1d23',
    borderRadius: '10px',
    border: '1px solid #2a2d35',
  },
  drawGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  drawCard: {
    backgroundColor: '#1a1d23',
    borderRadius: '10px',
    border: '1px solid #2a2d35',
    overflow: 'hidden',
  },
  headerCell: {
    flex: 1,
    fontSize: '12px',
    color: '#888',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
};

export default PublicLottery;
